import Typesense, { Client } from 'typesense';

export default class HeaderSearch extends HTMLElement {
  private isOpen = false;
  private inputEl: HTMLInputElement | undefined = undefined;
  private formEl: HTMLFormElement | undefined = undefined;
  private mode: 'desktop' | 'mobile' = 'mobile';
  private classActive = 'is-open';
  private minInputLength = 3;
  private typesenseClient: Client | null = null;
  private typesenseCollectionName = '';
  private typesenseSearchParams: Record<string, unknown> = {};
  private resultHitsContainer: HTMLElement | null = null;
  private requestTimer: number | null = null;

  constructor() {
    super();
  }

  listenBlurInput() {
    document.addEventListener('click', (e: MouseEvent) => {
      if (e?.target && !this.contains(e?.target as Node)) {
        this.hideInput();
      }
    });

    this.inputEl?.addEventListener('keydown', (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') this.hideInput();
      if (evt.key === 'Enter') {
        evt.preventDefault();
        this.onClickButtonSearch();
      }
    });
  }

  showInput() {
    this.isOpen = true;
    this.classList.toggle(this.classActive, true);
    setTimeout(() => {
      this.inputEl?.focus();
    }, 100);
  }

  hideInput() {
    this.isOpen = false;
    this.classList.remove(this.classActive);
    this.clearSuggestions();
  }

  private createTypesenseClient(): void {
    const typesenseParameters = this.dataset.typesenseParameters ?? '';
    const searchParams = this.dataset.searchParams ?? '';

    if (!typesenseParameters) {
      return;
    }

    try {
      const config = JSON.parse(typesenseParameters);
      if (config.host && config.port && config.protocol && config.appKey && config.indexName) {
        this.typesenseCollectionName = config.indexName;
        this.typesenseClient = new Typesense.Client({
          nodes: [
            {
              host: config.host,
              port: config.port,
              protocol: config.protocol,
            },
          ],
          apiKey: config.appKey,
          timeoutSeconds: 5,
        });
      }
    } catch (error) {
      console.warn('Invalid typesense configuration for header search', error);
    }

    if (searchParams) {
      try {
        this.typesenseSearchParams = JSON.parse(searchParams);
      } catch (error) {
        console.warn('Invalid search params for header search', error);
      }
    }
  }

  private async fetchSuggestions(query: string) {
    if (!this.typesenseClient || !this.typesenseCollectionName) {
      return;
    }

    const params: Record<string, unknown> = {
      q: query,
      query_by: 'q',
      sort_by: 'count:desc',
      per_page: 5,
      ...this.typesenseSearchParams,
    };

    try {
      const response = await this.typesenseClient.collections(this.typesenseCollectionName).documents().search(params as any) as any;
      const hits = Array.isArray(response.hits) ? response.hits : [];
      this.renderSuggestions(hits);
    } catch (error) {
      console.warn('Header search suggestions failed', error);
      this.clearSuggestions();
    }
  }

  private renderSuggestions(hits: any[]) {
    if (!this.resultHitsContainer) {
      return;
    }

    if (!hits.length) {
      this.clearSuggestions();
      return;
    }

    const suggestions = hits.map((hit) => {
      const query = typeof hit?.document?.q === 'string' ? hit.document.q : '';
      const url = `/recherche/${encodeURIComponent(query)}/`;
      return `
        <li>
          <a href="${url}" target="_self">
            <span class="icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12.0207 11.078L14.876 13.9327L13.9327 14.876L11.078 12.0207C10.0158 12.8721 8.69467 13.3353 7.33333 13.3333C4.02133 13.3333 1.33333 10.6453 1.33333 7.33333C1.33333 4.02133 4.02133 1.33333 7.33333 1.33333C10.6453 1.33333 13.3333 4.02133 13.3333 7.33333C13.3353 8.69467 12.8721 10.0158 12.0207 11.078ZM10.6833 10.5833C11.5294 9.71326 12.0019 8.54695 12 7.33333C12 4.75467 9.91133 2.66667 7.33333 2.66667C4.75467 2.66667 2.66667 4.75467 2.66667 7.33333C2.66667 9.91133 4.75467 12 7.33333 12C8.54695 12.0019 9.71326 11.5294 10.5833 10.6833L10.6833 10.5833Z" fill="currentColor"/>
              </svg>
            </span>
            <span class="label">${query}</span>
          </a>
        </li>
      `;
    }).join('');

    this.resultHitsContainer.innerHTML = `<ol class="hits-list">${suggestions}</ol>`;
    const suggestionsContainer = this.resultHitsContainer.closest('.result-hits-container');
    suggestionsContainer?.classList.add('has-suggestions');
  }

  private clearSuggestions() {
    if (!this.resultHitsContainer) {
      return;
    }

    this.resultHitsContainer.innerHTML = '';
    const suggestionsContainer = this.resultHitsContainer.closest('.result-hits-container');
    suggestionsContainer?.classList.remove('has-suggestions');
  }

  private onInputValueChange() {
    const value = this.inputEl?.value?.trim() ?? '';

    if (!value) {
      this.clearSuggestions();
      return;
    }

    if (this.requestTimer) {
      window.clearTimeout(this.requestTimer);
    }

    this.requestTimer = window.setTimeout(() => {
      void this.fetchSuggestions(value);
    }, 250);
  }

  getInputWidth() {
    const bounds = this.getBoundingClientRect();
    const pXRight = bounds.x + bounds.width;

    if (this.mode === 'desktop') {
      const logoEl = document.querySelector('.main-nav-logo');
      const logoElBounds = logoEl ? logoEl.getBoundingClientRect() : null;
      if (logoElBounds) {
        const pXLeft = logoElBounds.x + logoElBounds.width + 30;
        const width = Math.floor(pXRight - pXLeft);

        if (width > 0) return width;
      }
    } else {
      const pXLeft = 20;

      return Math.floor(pXRight - pXLeft);
    }
  }

  onWindowResize() {
    setTimeout(() => {
      this.hideInput();
      this.style.setProperty('--input-width', `${this.getInputWidth()}px`);
    }, 250);
  }

  onClickButtonSearch() {
    if (this.isOpen) {
      /*
      if (this.inputEl && this.inputEl.value?.length > this.minInputLength) {
        this.formEl?.requestSubmit();
      }*/
      this.formEl?.requestSubmit();
    } else {
      this.showInput();
    }
  }

  onFormSubmit(evt: SubmitEvent) {
    evt.preventDefault();
    const term = this.inputEl?.value?.trim() ?? '';
    const searchURL = this.formEl?.getAttribute('action') ?? '/recherche/';
    const termURI = term ? `${encodeURIComponent(term)}/` : '';

    window.location.href = `${searchURL}${termURI}`;
  }

  init() {
    const { mode } = this.dataset;
    const submitButton = this.querySelector('button.main-menu-search-link') as HTMLButtonElement;
    this.inputEl = this.querySelector('.input-wrapper input') as HTMLInputElement;
    this.formEl = this.querySelector('form') as HTMLFormElement;
    this.resultHitsContainer = this.querySelector('.result-hits');

    if (mode === 'desktop') this.mode = mode;

    this.createTypesenseClient();

    submitButton?.addEventListener('click', () => {
      submitButton.blur();
      this.onClickButtonSearch();
    });

    this.inputEl?.addEventListener('input', this.onInputValueChange.bind(this));

    this.listenBlurInput();
    this.onWindowResize();

    window.addEventListener('resize', this.onWindowResize.bind(this));
    this.formEl?.addEventListener('submit', this.onFormSubmit.bind(this));
  }

  connectedCallback() {
    setTimeout(() => {
      this.init();
    });
  }
}